/**
 * Tunnel session KDF (ADR-014) — the exact byte layout both peers must agree on.
 *
 * Kept pure and dependency-free (only tweetnacl) so it can be unit-tested from
 * both sides of a handshake, and so the iOS/Android ports have one unambiguous
 * reference instead of three prose descriptions.
 *
 *   v1: SHA-512("VoxioxTunnelV1" || shared || ephLow || ephHigh)
 *   v2: SHA-512("VoxioxTunnelV2" || shared || ephLow || ephHigh || idLow || idHigh)
 *
 * "low/high" is the byte-wise order of the two EPHEMERAL keys; identity keys
 * follow the same side order, so both peers build an identical input. A peer
 * without an identity (anonymous guest) contributes 32 zero bytes — what went
 * on the wire is what goes in the hash.
 *
 * Output: [0..20) prefix for the low side, [20..40) for the high side,
 *         [40..44) SAS bytes.
 */
import nacl from 'tweetnacl';

export const IDENTITY_LEN = 32;
export const PREFIX_LEN = 20;
export const ANONYMOUS_IDENTITY = new Uint8Array(IDENTITY_LEN);

function concatBytes(...arrays) {
  const total = arrays.reduce((n, a) => n + a.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const a of arrays) { out.set(a, off); off += a.length; }
  return out;
}

/** Constant-time-ish zero check — the value is public-ish, but keep it uniform. */
export function isAllZero(bytes) {
  let acc = 0;
  for (let i = 0; i < bytes.length; i++) acc |= bytes[i];
  return acc === 0;
}

export function comparePubKeys(a, b) {
  for (let i = 0; i < a.length; i++) {
    if (a[i] < b[i]) return -1;
    if (a[i] > b[i]) return 1;
  }
  return 0;
}

/**
 * @param {Uint8Array} shared        X25519 shared secret (nacl.box.before)
 * @param {Uint8Array} myEphemeral   our ephemeral public key
 * @param {Uint8Array} peerEphemeral peer's ephemeral public key
 * @param {Uint8Array|null} myIdentity   identity we advertised (null = anonymous)
 * @param {Uint8Array|null} peerIdentity identity the peer advertised
 * @param {boolean} v2               both sides speak handshake v2
 * @returns {{sendPrefix: Uint8Array, recvPrefix: Uint8Array, sasBytes: Uint8Array}}
 */
export function deriveSession({ shared, myEphemeral, peerEphemeral, myIdentity, peerIdentity, v2 }) {
  // Contributory behaviour: X25519 with a low-order peer key yields an all-zero
  // shared secret, which both sides would happily "agree" on — the attacker
  // knows it, so the call would be encrypted with a key of the attacker's
  // choosing. The signature in ADR-014 does not cover this for guests or v1
  // peers, so the check has to live here, in the derivation itself.
  if (!shared || shared.length !== 32 || isAllZero(shared)) {
    throw new Error('Degenerate shared secret — refusing to derive session');
  }

  const cmp = comparePubKeys(myEphemeral, peerEphemeral);
  if (cmp === 0) {
    // Identical ephemeral keys would give both sides the same send prefix and
    // reuse a keystream. Only reachable via reflection, but refuse it loudly.
    throw new Error('Peer ephemeral key equals ours — refusing to derive session');
  }

  // Our own identity coming back at us means the relay is reflecting, not
  // relaying: nothing legitimate produces it.
  if (myIdentity && peerIdentity && comparePubKeys(myIdentity, peerIdentity) === 0) {
    throw new Error('Peer identity equals ours — refusing to derive session');
  }

  const iAmLow = cmp < 0;

  const sortedEph = iAmLow
    ? concatBytes(myEphemeral, peerEphemeral)
    : concatBytes(peerEphemeral, myEphemeral);

  const tag = new TextEncoder().encode(v2 ? 'VoxioxTunnelV2' : 'VoxioxTunnelV1');

  let fullHash;
  if (v2) {
    const mine = myIdentity || ANONYMOUS_IDENTITY;
    const theirs = peerIdentity || ANONYMOUS_IDENTITY;
    const sortedIds = iAmLow ? concatBytes(mine, theirs) : concatBytes(theirs, mine);
    fullHash = nacl.hash(concatBytes(tag, shared, sortedEph, sortedIds));
  } else {
    fullHash = nacl.hash(concatBytes(tag, shared, sortedEph));
  }

  const prefixLow = fullHash.slice(0, PREFIX_LEN);
  const prefixHigh = fullHash.slice(PREFIX_LEN, PREFIX_LEN * 2);
  return {
    sendPrefix: iAmLow ? prefixLow : prefixHigh,
    recvPrefix: iAmLow ? prefixHigh : prefixLow,
    sasBytes: fullHash.slice(40, 44),
  };
}
